import { Component, ElementRef, Input, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';

import { ICON, ICON_STYLE } from '@core/util/icon';
import {
	buildDetailsInformation,
	getMortgagePrimaryLender,
} from '@core/util/mortgage';
import { Encumbrance } from '@models/encumbrance';
import { MatterFile, MortgageMatterFile } from '@models/matter-file';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgageWithAssociations,
	MortgageStep,
	TransactionType,
} from '@models/mortgage';
import { Lender, MortgageeType } from '@models/lender';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { UrlService } from '@services/url/url.service';
import { ClientsDispatch } from '@state/clients';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { Observable } from 'rxjs';

import { MortgageDetailsOverview } from '../mortgages.models';
import { NavigationService } from '@services/nav/nav.service';
import { MatterMortgageFormTemplatesState } from '@state/matter/matter_mortgages/matter-mortgage-forms/matter-mortgage-form-templates.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MortgageTabOption } from '../llc/components/mortgage-tabs/mortgage-tabs.component';

enum DetailNavType {
	MORTGAGE_DETAILS = 'mortgageDetails',
	PAYMENT = 'payment',
	REPORT = 'report',
	DOCUMENTS = 'documents',
	// LENDER_DETAILS = 'lenderDetails',
	// TERMS = 'terms',
}

const DetailsNavOptions: MortgageTabOption<DetailNavType>[] = [
	{ label: 'General Details', value: DetailNavType.MORTGAGE_DETAILS },
	{
		label: 'Terms & Payment',
		value: DetailNavType.PAYMENT,
		intercomAttr: 'terms-payment',
	},
	// { label: 'Lender details', value: DetailNavType.LENDER_DETAILS },
	// { label: 'Terms', value: DetailNavType.TERMS },
	{ label: 'Registration Details', value: DetailNavType.REPORT },
	{
		label: 'Forms',
		value: DetailNavType.DOCUMENTS,
		intercomAttr: 'mortgage-forms',
	},
];

@UntilDestroy()
@Component({
	selector: 'app-manual-mortgage',
	templateUrl: './manual.component.html',
	styleUrls: ['./manual.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class ManualMortgageComponent implements OnInit {
	@Input() set matterMortgage(matterMortgage: MatterMortgageWithAssociations) {
		this._matterMortgage = matterMortgage;
		this.lender = getMortgagePrimaryLender(this._matterMortgage);

		if (matterMortgage) {
			switch (matterMortgage.step) {
				case MortgageStep.RECEIVE_INSTRUCTIONS:
					this.currentStep = 0;
					break;
				case MortgageStep.SUBMIT_PRELIM:
					this.currentStep = 1;
					break;
				case MortgageStep.SUBMIT_FINAL_REPORT:
					this.currentStep = 2;
					break;
				default:
					this.currentStep = null;
			}
		}

		this.headerIcon = [
			MortgageeType.INSTITUTION,
			MortgageeType.COMPUTERSHARE,
		].includes(this.lender?.mortgageeType)
			? ICON.BUSINESS
			: ICON.PERSON;

		this.formsPageLink = UrlService.Matter_Documents_Forms(this.matterId);

		this.details = buildDetailsInformation(matterMortgage);

		this.isVTBMortgage = matterMortgage.transactionType === TransactionType.VTB;
	}

	@ViewChild('mortgageTabs', { static: true, read: ElementRef })
	mortgageTabs: ElementRef;

	public lender: Lender;

	@SelectSnapshot(MatterEncumbrancesState.getRemain)
	remainEncumbrances: Encumbrance[];
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterUploadedFilesSelectors.getMortgageDocuments)
	mortgageDocFilterFn$: Observable<(id: string) => MortgageMatterFile[]>;
	@Select(MatterMortgageFormTemplatesState.hasNewFlaggedMortgageForm)
	hasNewMortgageForm$: Observable<boolean>;
	public isVTBMortgage = false;
	public detailsNavOptions = DetailsNavOptions;
	public currentStep = 0;
	public selectedDetailNav = DetailNavType.MORTGAGE_DETAILS;
	public navOptions = DetailNavType;
	public _matterMortgage: MatterMortgageWithAssociations;
	public details: Partial<MortgageDetailsOverview>;
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;
	public priortyDescription: string;
	public formsPageLink: string;
	public mortgageeTypes = MortgageeType;
	public MatterMortgageInstructionSource = MatterMortgageInstructionSource;
	public showInfoSyncBanner = false;

	public documents$: Observable<MatterFile[]>;

	public showSummary = false;
	public headerIcon = ICON.BUSINESS;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private matDialog: MatDialog,
		private clientsDispatch: ClientsDispatch,
		private navService: NavigationService,
	) {}

	ngOnInit(): void {
		this.hasNewMortgageForm$.pipe(untilDestroyed(this)).subscribe((hasNew) => {
			const newDetailsNavOptions = this.detailsNavOptions.map((tab) => {
				if (tab.value === DetailNavType.DOCUMENTS) {
					tab.newFlag = hasNew;
				}
				return tab;
			});
			this.detailsNavOptions = newDetailsNavOptions;
		});
	}

	setMaturityDate($event): void {
		this.mortgageDispatch.setMaturityDate(this._matterMortgage.id, $event);
	}

	setTaxes($event): void {
		this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
			taxes: $event,
		});
	}

	toggleStep(step: number, selected: boolean): void {
		if (selected) {
			switch (step) {
				case 0:
					this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
						step: MortgageStep.RECEIVE_INSTRUCTIONS,
					});
					break;
				case 1:
					this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
						step: MortgageStep.SUBMIT_PRELIM,
					});
					break;
				case 2:
					this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
						step: MortgageStep.SUBMIT_FINAL_REPORT,
					});
					break;
			}
		} else {
			switch (step) {
				case 0:
					this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
						step: null,
					});
					break;
				default:
					this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
						step: MortgageStep.SUBMIT_PRELIM,
					});
					break;
			}
		}
	}

	goToInstructions(): void {
		this.navService.goToRemoteUrl(
			'https://unity.doprocess.com/login/home',
			true,
		);
	}

	setActingForMortgagee(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
			actingForMortgagee: value,
		});
	}

	setMMS(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._matterMortgage.id, {
			mms: value,
		});
	}

	toggleShowSummary() {
		this.showSummary = !this.showSummary;
	}

	viewAll() {
		this.selectedDetailNav = DetailNavType.PAYMENT;
		this.mortgageTabs.nativeElement.scrollIntoView({ behavior: 'smooth' });
	}

	gotoArea(path: DetailNavType) {
		this.selectedDetailNav = path;
		this.mortgageTabs.nativeElement.scrollIntoView({ behavior: 'smooth' });
	}

	updateSelectedTab(tab: DetailNavType) {
		this.selectedDetailNav = tab;
	}

	goToCloserSync() {
		this.navService.goToProfile_Integrations_CloserSync();
	}
}
